import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import {
  usersBulkGetUsers,
  usersGetUserById,
  v2UsersList,
} from "@/usersAndProjects-client/sdk.gen";
import type { ParticipantUser, User } from "../entities";

/**
 * Fetch user by ID (sub)
 * This hook automatically caches users so the same user isn't fetched multiple times
 */
export interface UseFetchUserResult {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchUser = (userId: string | null): UseFetchUserResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      const response = await usersGetUserById({
        path: { sub: userId },
      });
      return response.data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    user: data || null,
    isLoading,
    error: error as Error | null,
  };
};

/**
 * Fetch users in the authenticated user's tenant and transform for participant selection.
 *
 * The API derives the tenant from the authenticated session. currentTenantId is only used
 * to scope the React Query cache when the authenticated tenant changes.
 */
export interface UseFetchCurrentTenantUsersResult {
  users: ParticipantUser[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchCurrentTenantUsers = (
  currentTenantId: string | null | undefined,
  options?: { enabled?: boolean },
): UseFetchCurrentTenantUsersResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["users", "currentTenant", currentTenantId],
    queryFn: async () => {
      const response = await v2UsersList();
      // Transform to participant format
      return (
        response.data
          ?.filter((user) => user.isActive)
          .map((user) => ({
            id: user.sub || "",
            name: user.displayName || user.mail || "",
            email: user.mail || "",
          })) || []
      );
    },
    enabled: (options?.enabled ?? true) && !!currentTenantId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    users: data || null,
    isLoading,
    error: error as Error | null,
  };
};

/**
 * Fetch multiple users by their IDs in parallel
 */
export interface UseFetchUsersResult {
  usersMap: Map<string, User>;
  isLoading: boolean;
  errors: Error[];
}

export const useFetchUsers = (userIds: string[]): UseFetchUsersResult => {
  const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
  const sortedUserIds = uniqueUserIds.slice().sort();

  const { data, isLoading, error } = useQuery({
    queryKey: ["users", "bulk", sortedUserIds],
    queryFn: async () => {
      const response = await usersBulkGetUsers({
        body: {
          subs: sortedUserIds,
        },
      });
      return response.data || [];
    },
    enabled: sortedUserIds.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false, // Don't retry on failure - user might not exist
    refetchOnWindowFocus: false,
  });

  // Use ref to maintain stable usersMap reference
  const stableMapRef = useRef(new Map<string, User>());

  const newMap = new Map<string, User>();
  for (const user of data || []) {
    if (user && typeof user === "object" && "sub" in user) {
      newMap.set(user.sub, user);
    }
  }

  // Only update ref if the map contents actually changed
  // Compare map sizes and check if all keys exist (object references may change but IDs won't)
  const hasChanged =
    stableMapRef.current.size !== newMap.size ||
    Array.from(newMap.keys()).some((key) => !stableMapRef.current.has(key));

  if (hasChanged) {
    stableMapRef.current = newMap;
  }

  const errors: Error[] = error ? [error as Error] : [];

  return {
    usersMap: stableMapRef.current,
    isLoading,
    errors,
  };
};
